/* eslint-disable no-underscore-dangle */
import { format } from 'date-fns';
import theme from '@ui/configs/vuetify';
import { formatTooltipValue } from '@/helpers/formatters';

const { green500 } = theme.theme.themes.light.colors;

/**
 *
 * @param tooltipModel
 * @param {object} root0 object parameter
 * @param {object} root0.chartData
 * @param {boolean} root0.isFlippedAxis
 * @param {boolean} root0.showTotal
 * @param {string} root0.dateFormat
 * @param root0.titleFormatter
 */
function formatStackedBarChart(tooltipModel, {
  chartData,
  isFlippedAxis = false,
  showTotal = false,
  titleFormatter,
} = {}) {
  const { datasets, originalLabels } = chartData;
  const charts = [];

  tooltipModel.dataPoints.forEach((point) => {
    const value = datasets[point.datasetIndex].data[point.dataIndex];
    const { stack, type = 'bar' } = datasets[point.datasetIndex];

    const title = point.xLabel || originalLabels[point.dataIndex];
    const formattedTitle = titleFormatter(title);

    const { label } = datasets[point.datasetIndex];
    let valueToUse = value;
    const isTimeseries = isFlippedAxis ? typeof value === 'object' && typeof value.x === 'number' : typeof value === 'object' && typeof value.y === 'number';

    if (isTimeseries) {
      if (isFlippedAxis) {
        valueToUse = value.x;
      } else {
        valueToUse = value.y;
      }
    }

    if (charts[stack]) {
      charts[stack].bodyLines.push({
        value: valueToUse,
        label,
        datasetIndex: point.datasetIndex,
        index: point.dataIndex,
        datasetType: type,
      });
    } else {
      charts[stack] = {
        title: formattedTitle,
        bodyLines: [{
          value: valueToUse,
          label,
          datasetIndex: point.datasetIndex,
          index: point.dataIndex,
          datasetType: type,
        }],
      };
    }
  });

  if (showTotal) {
    return charts.map((chart) => {
      let value = 0;

      chart.bodyLines.forEach((bodyLine) => {
        value += Number(bodyLine.value) || 0;
      });

      const newBodyLines = [...chart.bodyLines, {
        ...chart.bodyLines[0],
        value,
        label: 'Total',
        customColor: green500,
      }];

      return { ...chart, bodyLines: newBodyLines };
    });
  }

  return charts;
}

/**
 *
 * @param tooltipModel
 * @param {object} root0 object parameter
 * @param {boolean} root0.isCompare
 * @param {object} root0.chartData
 * @param {boolean} root0.isFlippedAxis
 * @param root0.labelFormat
 */
function formatDonutChart(tooltipModel, {
  isCompare, chartData, isFlippedAxis = false, labelFormat = '',
}) {
  const charts = [];
  const { values, compareValues } = chartData;
  const bodyLines = tooltipModel.dataPoints.map((point) => {
    const value = values.datasets[point.datasetIndex].data[point.dataIndex];
    let valueToUse = value;
    const isTimeseries = isFlippedAxis ? typeof value === 'object' && typeof value.x === 'number' : typeof value === 'object' && typeof value.y === 'number';

    if (isTimeseries) {
      if (isFlippedAxis) {
        valueToUse = value.x;
      } else {
        valueToUse = value.y;
      }
    }

    return {
      value: formatTooltipValue(valueToUse, labelFormat),
      label: '',
    };
  });

  /* TODO: Hook up this and the compare date range
   to the date picker state when it supports comparison views */
  charts.push({ title: isCompare ? '1/2/22 - 2/2/22' : values.labels[tooltipModel.dataPoints[0].dataIndex], bodyLines });

  if (isCompare) {
    const comparisonBodyLines = tooltipModel.dataPoints.map((point) => {
      const value = compareValues.datasets[point.datasetIndex].data[point.dataIndex];
      let valueToUse = value;
      const isTimeseries = isFlippedAxis ? typeof value === 'object' && typeof value.x === 'number' : typeof value === 'object' && typeof value.y === 'number';

      if (isTimeseries) {
        if (isFlippedAxis) {
          valueToUse = value.x;
        } else {
          valueToUse = value.y;
        }
      }

      return {
        value: valueToUse,
        label: compareValues.labels[point.dataIndex],
      };
    });

    charts.push({ title: '1/2/21 - 2/2/21', bodyLines: comparisonBodyLines });
  }

  return charts;
}

/**
 *
 * @param tooltipModel
 * @param {object} root0 object parameter
 * @param {object} root0.chartData
 * @param {boolean} root0.isFlippedAxis
 * @param {string} root0.labelFormat
 */
function formatBasicChart(tooltipModel, { chartData, isFlippedAxis = false, labelFormat = '' }) {
  const charts = [];
  const { datasets, labels } = chartData;
  const isTimeseries = labelFormat === 'timestamp';
  const bodyLines = tooltipModel.dataPoints.map((point) => {
    const value = datasets[point.datasetIndex].data[point.dataIndex];
    let valueToUse = value;

    if (isTimeseries) {
      if (isFlippedAxis) {
        valueToUse = value.x;
      } else {
        valueToUse = value.y;
      }
    }
    return {
      value: formatTooltipValue(valueToUse, labelFormat),
      label: datasets?.[point.datasetIndex]?.label || '',
    };
  });

  const title = labels?.[tooltipModel?.dataPoints?.[0]?.dataIndex] || tooltipModel.title[0] || '';
  const formattedTitle = Array.isArray(title) ? title.join(' ') : title;

  charts.push({ title: isTimeseries ? format(new Date(`${title} 00:00`), 'MM/dd/yyyy') : formattedTitle, bodyLines });

  return charts;
}

const formatKey = {
  stackedBarChart: formatStackedBarChart,
  donut: formatDonutChart,
  basicChart: formatBasicChart,
};

const getBodyPadding = (index, length) => {
  if (index === 0 && length > 1) {
    return '0px 0px 0px 24px';
  }
  if (index === length - 1 && length > 1) {
    return '0px 24px 0px 0px';
  }
  if (length === 1) {
    return '0px 24px';
  }
  return '0px';
};

const getTitlePadding = (index, length) => {
  if (index === 0 && length > 1) {
    return '16px 0px 8px 24px';
  }
  if (index === length - 1 && length > 1) {
    return '16px 24px 8px 0px';
  }
  if (length === 1) {
    return '16px 24px 8px 24px';
  }
  return '16px 0px 8px 0px';
};

const newestArgs = {};

/**
 *
 * @param {string} type
 * @param {object} typeArgs
 * @param id
 */
export function customTooltip(type, typeArgs, id) {
  newestArgs[id] = typeArgs;

  return function generateTooltip({ tooltip: tooltipModel }) {
    // Tooltip Element
    let tooltipEl = document.getElementById('chartjs-tooltip');

    // Create element on first render
    if (!tooltipEl) {
      tooltipEl = document.createElement('div');
      tooltipEl.id = 'chartjs-tooltip';
      document.querySelector(`[data-widget-id=${id}]`).appendChild(tooltipEl);
    }

    // Hide if no tooltip
    if (tooltipModel.opacity === 0) {
      tooltipEl.style.opacity = 0;
      return;
    }

    // Call on hover if tooltip has custom onHover event in its config object

    if (newestArgs[id] && newestArgs[id].onHover) {
      newestArgs[id].onHover();
    }

    // Set caret Position
    tooltipEl.classList.remove('above', 'below', 'no-transform');
    if (tooltipModel.yAlign) {
      tooltipEl.classList.add(tooltipModel.yAlign);
    } else {
      tooltipEl.classList.add('no-transform');
    }

    // Set Text
    const formattedData = formatKey[type](tooltipModel, newestArgs[id]);
    let innerHtml = '<div style="display: flex; align-items: flex-start; justify-content: space-between;">';

    const filteredData = formattedData.filter((d) => d);
    filteredData.forEach(({ title, bodyLines }, dataIndex) => {
      const marginRight = dataIndex === 0 && filteredData.length > 1 ? '36px' : '0px';
      const titleBorderRadiius = dataIndex === 0 && filteredData.length > 1 ? '20px 0px 0px 0px' : '0px 20px 0px 0px';
      const width = 100 / filteredData.length;

      const bodyPadding = getBodyPadding(dataIndex, filteredData.length);
      const titlePadding = getTitlePadding(dataIndex, filteredData.length);

      // Dataset
      innerHtml += `<div style="display: flex; flex-direction: column; align-items: flex-start; justify-content: flex-start; box-sizing: border-box; width: ${width}%;">`;

      // Title
      innerHtml += `<div style="font-weight: 700; font-size: 16px; line-height: 20px; height: 44px; width: 100%; text-align: left; background: rgba(31, 45, 67, 0.05); color: #1F2D43; margin-bottom: 16px; padding: ${titlePadding}; border-radius: ${titleBorderRadiius};">${title}</div>`;

      // Body
      innerHtml += `<div style="display: flex; flex-direction: column; align-items: flex-start; padding: ${bodyPadding};">`;

      bodyLines.forEach(({
        value, label, datasetIndex, datasetType, customColor = '',
      }, i) => {
        if (value === null || value === undefined || Number.isNaN(value)) return;

        const tooltipModelBorderColor = tooltipModel?.labelColors?.[i]?.borderColor;
        const tooltipModelBackgroundColor = tooltipModel?.labelColors?.[i]?.backgroundColor;
        const datasetsBackgroundColor = this?._data?.datasets?.[datasetIndex]?.backgroundColor;

        let color = tooltipModelBorderColor !== '#FFFFFF' ? tooltipModelBorderColor : '';

        if (tooltipModelBackgroundColor && tooltipModelBackgroundColor !== '#FFFFFF') {
          color = tooltipModelBackgroundColor;
        }

        if (datasetsBackgroundColor && datasetsBackgroundColor !== '#FFFFFF') {
          color = datasetsBackgroundColor;
        }

        if (customColor) {
          color = customColor;
        }

        let colorCircle;

        // Body Line
        if (datasetType === 'line') {
          colorCircle = `<div style="position: relative; margin-right: 8px;">
            <div style="background-color: ${color}; width: 24px; height: 4px; border-radius: 10px;"></div>
            <div style="width: 8px; height: 8px; border-radius: 50%; position: fixed; top: -2px; left: 8px; background-color: ${color};"></div>
          </div>`;
        } else {
          colorCircle = `<div style="background-color: ${color}; width: 24px; height: 8px; border-radius: 10px; margin-right: 8px;"></div>`;
        }

        const bodySpan = `<div style="display: flex; align-items: center; margin-right: ${marginRight}">
          <span style="font-weight: bold; font-size: 16px; line-height: 20px; color: #1F2D43; margin-right: 8px;">${value}</span>  
          <span style="font-size: 16px; line-height: 20px; color: #1F2D43;">${label}</span>
        </div>`;

        innerHtml += `<div style="display: flex; align-items: center; justify-content: flex-start; margin-bottom: 12px;">${colorCircle}${bodySpan}</div>`;
      });

      innerHtml += '</div></div>';
    });

    innerHtml += '</div>';

    tooltipEl.innerHTML = innerHtml;

    // `this` will be the overall tooltip
    const position = this.chart.canvas.getBoundingClientRect();

    // Display, position, and set styles for font
    tooltipEl.style.opacity = 1;
    tooltipEl.style.backgroundColor = '#ffffff';
    tooltipEl.style.boxShadow = '0px 0px 40px rgba(31, 45, 67, 0.1)';
    tooltipEl.style.borderRadius = '20px';
    tooltipEl.style.overflow = 'hidden';
    tooltipEl.style.padding = '0px 0px 16px 0px';
    tooltipEl.style.position = 'fixed';
    tooltipEl.style.zIndex = 1;

    if (type === 'stackedBarChart') {
      tooltipEl.style.left = `${position.left + window.pageXOffset + tooltipModel.caretX}px`;
      tooltipEl.style.top = `${position.top + window.pageYOffset + tooltipModel.caretY}px`;
    } else {
      tooltipEl.style.left = `${position.left + tooltipModel._eventPosition.x}px`;
      tooltipEl.style.top = `${position.top + tooltipModel._eventPosition.y - 60}px`;
    }

    // Variables needed to decide if the bottom of the tooltip is off the screen and get offset
    const bodyRect = document.body.getBoundingClientRect();
    const tooltipRect = tooltipEl.getBoundingClientRect();
    const adjustToShowWholeTooltip = tooltipRect.top > (bodyRect.height - tooltipEl.clientHeight);
    const offset = tooltipRect.bottom - bodyRect.bottom + 10;

    // Offset top to allow full tooltip to be visible with 10px of padding
    if (adjustToShowWholeTooltip) {
      tooltipEl.style.top = `${position.top + window.pageYOffset + tooltipModel.caretY - offset}px`;
    }

    tooltipEl.style.fontFamily = tooltipModel.options.bodyFont.family;
    tooltipEl.style.fontSize = `${tooltipModel.options.bodyFont.size}px`;
    tooltipEl.style.fontStyle = tooltipModel.options.bodyFont.style;
    tooltipEl.style.pointerEvents = 'none';
    tooltipEl.style.transition = 'opacity 0.2s ease-in-out';
  };
}
